import { useStepper } from '@common/context/StepperContext'
import { SIZING } from '@common/theme/sizing'
import { SPACING } from '@common/theme/spacing'
import { Typography, Stack, Chip, Grid2 as Grid } from '@mui/material'
import { Suspense, lazy, useEffect } from 'react'
import { Breadcrumb, BreadcrumbItem } from '@common/components/Breadcrumb'
import { RouteKeyEnum } from 'src/routes'
import { useParams } from 'react-router-dom'
import { useStudyDetails } from '@features/study_details/components/StudyDetails/hooks'
import { Home } from '@mui/icons-material'
import { COLLECT_AND_HOLD_STUDY_TYPE } from '@study_setup/constants/form_dropdown_options'

const StudyOverview = lazy(() => import('./StudyOverview'))
const TimePointDefinition = lazy(() => import('./TimePointDefinition'))
const StudyAdherence = lazy(() => import('../StudyAdherence/components'))
const SubjectConfiguration = lazy(() => import('../SubjectConfiguration/components'))
const QualityControl = lazy(() => import('./QualityControl'))
const DICOMTagsDeId = lazy(() => import('./DICOMTagsDeId'))
const ReadDesign = lazy(() => import('./ReadDesign'))

export default function CreateOrUpdateStudy() {
  const { activeStep, setActiveStep } = useStepper()
  const { studyId } = useParams<{ studyId?: string }>()
  const { data: studyDetails } = useStudyDetails(studyId!)
  const isCollectAndHold = studyDetails?.type === COLLECT_AND_HOLD_STUDY_TYPE

  useEffect(() => {
    setActiveStep(0)
    return () => {
      setActiveStep(0)
    }
  }, [])

  const stepComponents = [
    () => <StudyOverview />,
    () => <TimePointDefinition />,
    () => <SubjectConfiguration />,
    () => <StudyAdherence />,
    () => <QualityControl />,
    () => <DICOMTagsDeId />,
    ...(isCollectAndHold ? [] : [() => <ReadDesign studyDetails={studyDetails ?? null} />]),
  ]
  const StepComponent = stepComponents[activeStep]

  const BreadcrumbItems = [
    {
      title: 'Home',
      route_key: RouteKeyEnum.HOME,
      icon: Home,
    },
    {
      title: studyDetails ? `${studyDetails.nickname} (${studyDetails.user_facing_study_id})` : '',
      route_key: RouteKeyEnum.STUDY_DETAILS,
      params: { studyId: studyId || '' },
    },
  ] as BreadcrumbItem[]

  const getGridWidth = () => {
    if (activeStep === 0 || activeStep === 4 || activeStep === 5) {
      return 8
    }

    return 12
  }

  const getGridOffset = () => {
    if (activeStep === 0 || activeStep === 4 || activeStep === 5) {
      return 2
    }

    return 0
  }

  return (
    <Stack>
      {studyDetails?.nickname && (
        <Stack>
          <Breadcrumb items={BreadcrumbItems} sx={{ paddingBottom: 2 }} />
        </Stack>
      )}
      <Grid container>
        <Grid size={{ xs: SIZING.gridSizeXxl, lg: getGridWidth() }} offset={{ xs: 0, lg: getGridOffset() }}>
          <Stack direction="row" spacing={SPACING.spacingLg} alignItems="center" mb={SPACING.spacingXxl}>
            <Typography variant="h4">Create a clinical study</Typography>
            <Chip label="Study setup" color="info-alt" />
          </Stack>
        </Grid>
      </Grid>

      <Suspense fallback={<div>Loading...</div>}>{StepComponent ? StepComponent() : null}</Suspense>
    </Stack>
  )
}
